import React, { useState, useEffect } from 'react';
import { supabase } from '../src/lib/supabaseClient';
import { api } from '../services/apiClient';
import type { AuthLang } from './AuthModal';
import {
  AUTH_LOGIN_CAROUSEL_INTERVAL_MS,
  AUTH_LOGIN_CAROUSEL_SRC,
} from '../utils/authLoginCarousel';

/** 文案仅中英完整；其余语种回退英文 */
type SignUpLang = 'en' | 'zh';

interface SignUpProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  language?: AuthLang;
  onSwitchToLogin?: () => void;
  /** 与官网 / 实验室 localStorage theme 一致；默认深色保持旧版外观 */
  isDarkMode?: boolean;
}

const copy: Record<SignUpLang, {
  enterCode: string;
  emailNotConfirmed: string;
  welcome: string;
  verifyFailed: string;
  sendCodeFailed: (x: string) => string;
  registerFailed: (x: string) => string;
  unexpectedError: (x: string) => string;
  codeSent: string;
  codeSentDev: (code: string) => string;
  titleRegister: string;
  titleVerify: string;
  descRegister: string;
  descVerify: string;
  verificationCode: string;
  placeholderCode: string;
  create: string;
  verify: string;
  sending: string;
  verifying: string;
  signIn: string;
  hidePwd: string;
  showPwd: string;
  labelEmail: string;
  labelPassword: string;
  footerHint: string;
}> = {
  en: {
    enterCode: 'Please enter verification code',
    emailNotConfirmed: 'Verification code verified! However, your email is not confirmed. Please check your email and click the confirmation link to complete registration.',
    welcome: 'Registration and verification successful! Welcome!',
    verifyFailed: 'Verification failed',
    sendCodeFailed: (x) => `Failed to send verification code: ${x}`,
    registerFailed: (x) => `Registration failed: ${x}`,
    unexpectedError: (x) => `Unexpected error: ${x}`,
    codeSent: 'Verification code has been sent to your email. Please check your email and enter the code below to verify.',
    codeSentDev: (code) => `Verification code has been sent to your email. Please check your email and enter the code below to verify.`,
    titleRegister: 'REGISTER',
    titleVerify: 'VERIFY',
    descRegister: 'Provide access credentials for system entry.',
    descVerify: 'Enter the 6-digit code sent to your email.',
    verificationCode: 'VERIFICATION CODE',
    placeholderCode: '6-digit code',
    create: 'CREATE',
    verify: 'VERIFY',
    sending: 'Sending...',
    verifying: 'Verifying...',
    signIn: 'SIGN IN',
    hidePwd: 'Hide password',
    showPwd: 'Show password',
    labelEmail: 'EMAIL',
    labelPassword: 'PASSWORD',
    footerHint: 'Already registered? Enter email and password, then click\nLogin.',
  },
  zh: {
    enterCode: '请输入验证码',
    emailNotConfirmed: '验证码已验证！但您的邮箱未确认。请检查您的邮箱并点击确认链接以完成注册。',
    welcome: '注册和验证成功！欢迎使用！',
    verifyFailed: '验证失败',
    sendCodeFailed: (x) => `发送验证码失败: ${x}`,
    registerFailed: (x) => `注册失败: ${x}`,
    unexpectedError: (x) => `意外错误: ${x}`,
    codeSent: '验证码已发送到您的邮箱，请前往邮箱查看并输入验证码完成验证。',
    codeSentDev: (code) => `验证码已发送到您的邮箱，请前往邮箱查看并输入验证码完成验证。`,
    titleRegister: '注册',
    titleVerify: '验证',
    descRegister: '填写邮箱与密码以完成注册。',
    descVerify: '请输入发送到您邮箱的 6 位验证码。',
    verificationCode: '验证码',
    placeholderCode: '6 位验证码',
    create: '创建',
    verify: '验证',
    sending: '发送中...',
    verifying: '验证中...',
    signIn: '登录',
    hidePwd: '隐藏密码',
    showPwd: '显示密码',
    labelEmail: '邮箱',
    labelPassword: '密码',
    footerHint: '已注册过的用户，输入邮箱和密码，点击登录；输入邮箱 6 位验证码后，点击验证。',
  },
};

const SIGNUP_LANGS: SignUpLang[] = ['en', 'zh'];

/** 与登录页相同：左侧轮播资源与间隔（路径见 utils/authLoginCarousel.ts） */
const LOGIN_CAROUSEL_INTERVAL_MS = AUTH_LOGIN_CAROUSEL_INTERVAL_MS;
const LOGIN_CAROUSEL_SRC = AUTH_LOGIN_CAROUSEL_SRC;

const SignUp: React.FC<SignUpProps> = ({ onSuccess, onError, language = 'en', onSwitchToLogin, isDarkMode = true }) => {
  const lang: SignUpLang = SIGNUP_LANGS.includes(language as SignUpLang) ? (language as SignUpLang) : 'en';
  const t = copy[lang];
  const cjkHero = lang === 'zh';

  const [carouselIdx, setCarouselIdx] = useState(0);
  useEffect(() => {
    if (LOGIN_CAROUSEL_SRC.length <= 1) return;
    const id = window.setInterval(() => {
      setCarouselIdx((i) => (i + 1) % LOGIN_CAROUSEL_SRC.length);
    }, LOGIN_CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  // 已注册用户点击“登录”时，走验证码登录流程，而不是跳转到单独的登录卡片
  const startVerificationForLogin = async () => {
    if (!email || !password) {
      // 需要先填写邮箱和密码
      setMessage(t.unexpectedError(''));
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const codeResult = await api.sendVerificationCode(email, password);
      if (!codeResult.success) {
        setMessage(t.sendCodeFailed(codeResult.error || ''));
        setLoading(false);
        return;
      }

      setNeedsVerification(true);
      setCodeSent(true);
      if (codeResult.code && (import.meta.env.DEV || process.env.NODE_ENV === 'development')) {
        console.log('[Dev] Verification code:', codeResult.code);
      }
      setMessage(t.codeSent);
    } catch (err: any) {
      setMessage(t.unexpectedError(err?.message || ''));
      if (onError) onError(err?.message || '');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (needsVerification && codeSent) {
      if (!verificationCode) {
        setMessage(t.enterCode);
        return;
      }
      setLoading(true);
      setMessage('');
      try {
        const result = await api.verifyCode(email, verificationCode, password);
        if (result.success) {
          if (result.emailNotConfirmed) {
            setMessage(t.emailNotConfirmed);
            setNeedsVerification(false);
            setCodeSent(false);
            setVerificationCode('');
            return;
          }
          const { data, error } = await supabase.auth.signInWithPassword({ email, password });
          if (error) {
            if (error.message?.includes('Email not confirmed') || error.message?.includes('email_not_confirmed') || error.message?.includes('未确认')) {
              setMessage(t.emailNotConfirmed);
              setNeedsVerification(false);
              setCodeSent(false);
              setVerificationCode('');
              return;
            }
            throw error;
          }
          if (data.user) {
            setMessage(t.welcome);
            if (onSuccess) setTimeout(() => onSuccess(), 1500);
          }
        } else {
          throw new Error(result.error || t.verifyFailed);
        }
      } catch (error: any) {
        setMessage(error.message || t.verifyFailed);
        if (onError) onError(error.message || t.verifyFailed);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const codeResult = await api.sendVerificationCode(email, password, { register: true });
      if (!codeResult.success) {
        setMessage(t.sendCodeFailed(codeResult.error || codeResult.message || ''));
        setLoading(false);
        return;
      }
      setNeedsVerification(true);
      setCodeSent(true);
      if (codeResult.code && (import.meta.env.DEV || process.env.NODE_ENV === 'development')) {
        console.log('[Dev] Verification code:', codeResult.code);
      }
      setMessage(t.codeSent);
    } catch (err: any) {
      setMessage(t.unexpectedError(err?.message || ''));
      if (onError) onError(err?.message || '');
    } finally {
      setLoading(false);
    }
  };

  const accentGold = '#D7CDA1';
  const isDark = isDarkMode;
  const bgColor = isDark ? '#000000' : '#fafafa';
  const textPrimary = isDark ? '#FFFFFF' : '#111111';
  const textSecondary = isDark ? '#888888' : '#666666';
  const borderDark = isDark ? '#222222' : '#e5e5e5';
  const placeholderMuted = isDark ? '#444444' : '#94a3b8';
  const msgSuccessBg = isDark ? 'rgba(34, 80, 34, 0.5)' : '#ecfdf5';
  const msgSuccessBorder = isDark ? '#2d5a2d' : '#6ee7b7';
  const msgSuccessText = isDark ? '#86efac' : '#047857';
  const msgErrorBg = isDark ? 'rgba(80, 34, 34, 0.5)' : '#fef2f2';
  const msgErrorBorder = isDark ? '#5a2d2d' : '#fecaca';
  const msgErrorText = isDark ? '#fca5a5' : '#b91c1c';

  return (
    <div
      className="login-split-screen"
      style={{
        fontFamily: "'SF Mono', Monaco, Inconsolata, 'Roboto Mono', monospace",
        background: bgColor,
        color: textPrimary,
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&family=Space+Grotesk:wght@400;500;700&family=JetBrains+Mono:wght@400;500&display=swap');
        .login-split-screen * { box-sizing: border-box; -webkit-font-smoothing: antialiased; }
        .login-split-screen .split-main { flex: 1; display: grid; grid-template-columns: 1fr; min-height: 400px; }
        @media (min-width: 1024px) {
          .login-split-screen .split-main { grid-template-columns: minmax(0, calc(100% * 1.28 / 2.28 + 35px)) minmax(0, 1fr); gap: 0; min-height: 100%; }
          .login-split-screen .left-panel { padding: 0; position: relative; overflow: hidden; min-height: 0; }
          .login-split-screen .split-spacer { display: none; }
          .login-split-screen .right-panel { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 2rem 3rem; min-width: 0; }
        }
        .login-split-screen .left-panel { padding: 0; position: relative; overflow: hidden; min-height: 280px; border-bottom: none; }
        @media (max-width: 1023px) {
          .login-split-screen .right-panel { padding: 2rem 1.5rem; }
        }
        .login-split-screen .login-carousel-wrap { position: absolute; inset: 0; background: ${isDark ? '#0a0a0a' : '#e4e4e7'}; }
        .login-split-screen .login-carousel-slide { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: opacity 0.5s ease; pointer-events: none; }
        .login-split-screen .login-carousel-scrim { position: absolute; inset: 0; pointer-events: none; background: ${
          isDark
            ? 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.35) 100%)'
            : 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(250,250,250,0.5) 100%)'
        }; }
        .login-split-screen .form-container { width: min(100%, 518px); margin-left: auto; margin-right: auto; animation: signupFadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards; opacity: 0; }
        @keyframes signupFadeIn { to { opacity: 1; } }
        .login-split-screen .form-header { margin-bottom: 2rem; }
        .login-split-screen .form-title { font-size: 2.73rem; font-weight: 500; letter-spacing: -0.02em; margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.75rem; }
        .login-split-screen .form-title-en { font-family: 'Space Grotesk', sans-serif; letter-spacing: 0.05em; font-size: 1em; }
        .login-split-screen .form-desc { font-size: 0.875rem; color: ${textSecondary}; line-height: 1.5; }
        .login-split-screen .input-group { margin-bottom: 1.5rem; position: relative; }
        .login-split-screen .input-label { display: flex; justify-content: space-between; font-size: 0.75rem; font-weight: 600; letter-spacing: 0.05em; margin-bottom: 0.5rem; color: ${textSecondary}; text-transform: uppercase; }
        .login-split-screen .input-label.cjk-label { text-transform: none; letter-spacing: 0.02em; }
        .login-split-screen .tech-input { width: 100%; background: transparent; border: none; border-bottom: 1px solid ${borderDark}; color: ${textPrimary}; font-size: 1rem; padding: 0.75rem 0; font-family: inherit; transition: border-color 0.3s ease; border-radius: 0; }
        .login-split-screen .tech-input:focus { outline: none; border-bottom-color: ${accentGold}; }
        .login-split-screen .tech-input::placeholder { color: ${placeholderMuted}; }
        .login-split-screen .tech-input:disabled { opacity: 0.6; cursor: not-allowed; }
        .login-split-screen .submit-btn { width: 100%; background-color: ${accentGold}; color: ${isDark ? bgColor : '#111111'}; border: none; padding: 1.25rem; font-family: 'Space Grotesk', sans-serif; font-size: 1.66rem; font-weight: 500; letter-spacing: 0.05em; text-transform: uppercase; cursor: pointer; display: flex; justify-content: space-between; align-items: center; transition: all 0.3s ease; margin-top: 2rem; border-radius: 0.375rem; }
        .login-split-screen .submit-btn.cjk-btn { text-transform: none; }
        .login-split-screen .submit-btn:hover:not(:disabled) { background-color: ${textPrimary}; color: ${isDark ? bgColor : '#ffffff'}; }
        .login-split-screen .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .login-split-screen .submit-btn .arrow { transition: transform 0.3s ease; }
        .login-split-screen .submit-btn:hover:not(:disabled) .arrow { transform: translateX(5px); }
        .login-split-screen .form-footer { margin-top: 1.5rem; display: flex; justify-content: space-between; align-items: center; gap: 1rem; font-size: 0.875rem; border-top: 1px solid ${borderDark}; padding-top: 1.25rem; flex-wrap: wrap; }
        .login-split-screen .sign-up-link { color: ${textPrimary}; text-decoration: none; font-weight: 500; display: inline-flex; align-items: center; gap: 0.5rem; transition: color 0.3s ease; cursor: pointer; background: none; border: none; font-size: inherit; font-family: inherit; }
        .login-split-screen .sign-up-link:hover { color: ${accentGold}; }
        .login-split-screen .sign-up-link .en { font-family: 'Space Grotesk', sans-serif; font-size: 1.66rem; font-weight: 500; letter-spacing: 0.05em; }
        .login-split-screen .form-footer-hint { font-size: 0.875rem; color: ${textSecondary}; white-space: pre-line; max-width: 20em; line-height: 1.5; flex: 1; min-width: 12rem; }
        .login-split-screen .msg-box { margin-top: 1rem; padding: 0.75rem; font-size: 0.875rem; border: 1px solid; white-space: pre-line; }
        .login-split-screen .msg-box.success { background: ${msgSuccessBg}; border-color: ${msgSuccessBorder}; color: ${msgSuccessText}; }
        .login-split-screen .msg-box.error { background: ${msgErrorBg}; border-color: ${msgErrorBorder}; color: ${msgErrorText}; }
        .login-split-screen .pwd-toggle { position: absolute; right: 0; top: 50%; transform: translateY(-50%); background: none; border: none; color: ${textSecondary}; cursor: pointer; padding: 0.25rem; }
        .login-split-screen .pwd-toggle:hover { color: ${textPrimary}; }
      `}</style>

      <main className="split-main">
        <section className="left-panel" aria-label="Gallery">
          <div className="login-carousel-wrap">
            {LOGIN_CAROUSEL_SRC.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className="login-carousel-slide"
                style={{ opacity: i === carouselIdx ? 1 : 0 }}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            ))}
            <div className="login-carousel-scrim" aria-hidden />
          </div>
        </section>
        <div className="split-spacer" aria-hidden />
        <section className="right-panel">
          <div className="form-container">
            <div className="form-header">
              <h2 className="form-title">
                <span className="form-title-en">{needsVerification ? t.titleVerify : t.titleRegister}</span>
              </h2>
              <p className="form-desc">
                {needsVerification ? t.descVerify : t.descRegister}
              </p>
            </div>

            <form onSubmit={handleSignUp}>
              <div className="input-group">
                <div className={`input-label ${cjkHero ? 'cjk-label' : ''}`}>
                  <div>{t.labelEmail}</div>
                  <span style={{ color: accentGold }}>*</span>
                </div>
                <input
                  type="email"
                  className="tech-input"
                  placeholder="name@domain.com"
                  required
                  disabled={needsVerification}
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="input-group">
                <div className={`input-label ${cjkHero ? 'cjk-label' : ''}`}>
                  <div>{t.labelPassword}</div>
                  <span style={{ color: accentGold }}>*</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="tech-input"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    disabled={needsVerification}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {!needsVerification && (
                    <button type="button" className="pwd-toggle" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? t.hidePwd : t.showPwd}>
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {needsVerification && codeSent && (
                <div className="input-group">
                  <div className={`input-label ${cjkHero ? 'cjk-label' : ''}`}>
                    <div>{t.verificationCode}</div>
                    <span style={{ color: accentGold }}>*</span>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="tech-input"
                    placeholder={t.placeholderCode}
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    maxLength={6}
                    autoComplete="off"
                  />
                </div>
              )}

              {message && (
                <div className={`msg-box ${(message.includes('success') || message.includes('成功') || message.includes('sent') || message.includes('发送')) ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                className={`submit-btn ${cjkHero ? 'cjk-btn' : ''}`}
                disabled={loading || (needsVerification && verificationCode.length < 6)}
              >
                <span>
                  {loading ? (needsVerification ? t.verifying : t.sending) : needsVerification ? t.verify : t.create}
                </span>
                <span className="arrow">→</span>
              </button>
            </form>

            {!(needsVerification && verificationCode.length === 6) && (
              <div className="form-footer">
                <div className="form-footer-hint">{t.footerHint}</div>
                {onSwitchToLogin && (
                  <button type="button" className="sign-up-link" onClick={startVerificationForLogin}>
                    <span className="en">{t.signIn}</span> →
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default SignUp;
